import { useEffect, useMemo } from 'react';
import { ErrorSchema, FieldProps } from '@rjsf/utils';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { Icon } from 'src/components/Icon';
import { Button } from 'src/components/ui';
import { ScheduleRow as Row, duplicateIndexes, newRow, normalizeRow } from './scheduleRowRules';
import { ScheduleRow, ScheduleTableHeader } from './ScheduleRow';
import useScheduleOptions, { ScheduleLookup } from './useScheduleOptions';

const NO_ROWS: Row[] = [];

/** Every `__errors` message under one row's error schema (e.g. a required schedule rjsf flagged). */
function collectErrors(errorSchema?: ErrorSchema): string[] {
  if (!errorSchema) return [];

  return Object.entries(errorSchema).flatMap(([key, value]) =>
    key === '__errors' ? ((value as unknown) as string[]) : collectErrors(value as ErrorSchema)
  );
}

/**
 * Edits a Reporting Requirements schedule list (`data.schedules` / `data.inventorySchedules`) as one table row per
 * schedule, keeping each row's frequency, year type and due date consistent (see `scheduleRowRules`).
 */
function OcifRequiredSchedulesField({ schema, formData, onChange, registry, fieldPathId, readonly, disabled, errorSchema }: FieldProps) {
  useLingui(); // re-render on locale change; labels come from the global t
  const readOnly = !!(readonly || disabled);
  const fieldId = fieldPathId?.$id ?? '';
  const lookup = (schema.items as { properties?: { schedule?: { lookup?: ScheduleLookup } } } | undefined)?.properties?.schedule?.lookup;
  const { options, loading, error } = useScheduleOptions(lookup, registry.formContext, fieldId);

  const rawRows: Row[] = Array.isArray(formData) ? formData : NO_ROWS;
  const rows = useMemo(() => rawRows.map(normalizeRow), [rawRows]);
  const needsNormalising = rows.some((row, index) => row !== rawRows[index]);
  const duplicates = useMemo(() => duplicateIndexes(rows), [rows]);

  const emit = (nextRows: Row[]) => onChange(nextRows, fieldPathId.path);

  // A loaded row that breaks the frequency rules is shown corrected; save it corrected too.
  useEffect(() => {
    if (needsNormalising && !readOnly) emit(rows);
  }, [needsNormalising, readOnly]);

  const rowErrors = (index: number) => [
    ...(duplicates.has(index) ? [t`This schedule is already listed with the same frequency.`] : []),
    ...collectErrors((errorSchema as Record<number, ErrorSchema> | undefined)?.[index]),
  ];

  return (
    <div className="@container">
      {schema.title && <div className="form-subtitle mb-2">{schema.title}</div>}
      {error && (
        <p className="text-danger small mb-2">
          <Trans>Could not load the schedule list.</Trans>
        </p>
      )}
      {rows.length ? (
        <>
          <ScheduleTableHeader readOnly={readOnly} />
          {rows.map((row, index) => (
            <ScheduleRow
              key={index}
              id={`${fieldId}_${index}`}
              row={row}
              options={options}
              loadingOptions={loading}
              errors={rowErrors(index)}
              onChange={readOnly ? undefined : changed => emit(rows.map((current, i) => (i === index ? normalizeRow(changed) : current)))}
              onRemove={readOnly ? undefined : () => emit(rows.filter((_, i) => i !== index))}
            />
          ))}
        </>
      ) : (
        <p className="text-muted small mb-2">
          <Trans>No schedules required.</Trans>
        </p>
      )}
      {!readOnly && (
        <Button color="link" size="sm" className="px-0 mt-2" onClick={() => emit([...rows, newRow()])}>
          <Icon icon="Plus" weight="regular" className="w-3.5 h-3.5" /> <Trans>Add schedule</Trans>
        </Button>
      )}
    </div>
  );
}

export default OcifRequiredSchedulesField;
